import { useCallback, useState, useEffect } from 'react'
import { loadGoogleApiScripts, initializePickerApi } from 'api/services/GoogleApi'
import { REACT_APP_GOOGLE_CLIENT_ID } from 'env'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { GoogleDriveClient } from 'api/clients/GoogleDriveClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

export interface PickerDocument {
  id: string
  name: string
  mimeType: string
  url?: string
}

export interface PickerSelection {
  files: PickerDocument[]
  folders: PickerDocument[]
}

interface UseGoogleDrivePickerReturn {
  openPicker: (onSelect: (selection: PickerSelection) => void) => void
  isLoading: boolean
  isConnected: boolean
}

const FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder'

const ALLOWED_MIME_TYPES = [
  FOLDER_MIME_TYPE,
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
  'video/mp4',
  'video/mpeg',
  'video/quicktime',
  'video/webm',
  'application/pdf',
  'text/plain',
  'text/markdown',
].join(',')

export const useGoogleDrivePicker = (): UseGoogleDrivePickerReturn => {
  const [isReady, setIsReady] = useState(false)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const workspaceId = useCurrentWorkspaceId()

  const { data: integrationsData } = useExternalIntegrations()
  const googleDriveIntegration = integrationsData?.external_integrations?.find(
    (integration) => integration.provider === ExternalIntegrationEnum.GoogleDrive,
  )

  useEffect(() => {
    if (!googleDriveIntegration || !workspaceId) return

    const initialize = async () => {
      await loadGoogleApiScripts()
      await initializePickerApi()
      const response = await GoogleDriveClient.getAccessToken({
        workspaceId,
        integrationId: googleDriveIntegration.id,
      })
      if (response.type === 'success') {
        setAccessToken(response.data.access_token)
        setIsReady(true)
      }
    }

    initialize()
  }, [googleDriveIntegration, workspaceId])

  const openPicker = useCallback(
    (onSelect: (selection: PickerSelection) => void) => {
      if (!accessToken || !window.google?.picker) return

      const docsView = new window.google.picker.DocsView()
        .setMode(window.google.picker.DocsViewMode.LIST)
        .setIncludeFolders(true)
        .setOwnedByMe(true)
        .setSelectFolderEnabled(true)
        .setMimeTypes(ALLOWED_MIME_TYPES)

      const picker = new window.google.picker.PickerBuilder()
        .addView(docsView)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .setMaxItems(100)
        .setOAuthToken(accessToken)
        .setAppId(REACT_APP_GOOGLE_CLIENT_ID.split('-')[0])
        .setCallback((data: google.picker.ResponseObject) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const documents = (data.docs || []) as PickerDocument[]
            onSelect({
              files: documents.filter((doc) => doc.mimeType !== FOLDER_MIME_TYPE),
              folders: documents.filter((doc) => doc.mimeType === FOLDER_MIME_TYPE),
            })
          }
        })
        .build()

      picker.setVisible(true)
    },
    [accessToken],
  )

  return {
    openPicker,
    isLoading: !isReady,
    isConnected: Boolean(googleDriveIntegration),
  }
}
